import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { UrlService } from '@service/url/url.service';
import {
	C4ListResponse,
	GET_TASK_FILTER,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	ProjectDTO,
	RestService,
	TASK_FILTER_CONSTANTS,
	TASK_STATUS_UUID,
	TASK_STATUS_UUID_MAP,
	TASK_TABLE,
	TaskDTO,
	TaskTable,
	TaskUpdateRequest,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { TaskService } from '@service/task/task.service';
import { UsersService } from '@service/users/users.service';

@Component({
	selector: 'app-tasks-infinite-scroll',
	templateUrl: './tasks-infinite-scroll.component.html',
	styleUrls: ['./tasks-infinite-scroll.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasksInfiniteScrollComponent implements OnInit {
	@Input() projectId = null;
	@Input() projectDTO: ProjectDTO = null;
	@Input() carrier: any = null;
	@Input() userAssignedToCarrier: number = null;

	@Output() totalNumberOfTasks: EventEmitter<any> = new EventEmitter();

	isLoading: boolean = false;
	tasksList: Array<TaskTable> = null;

	hasNext = false;
	page = 0;
	size = 25;

	selectedTasksIds: Array<string> = [];
	selectedTasks: Array<TaskTable> = [];
	statusFilter: Array<any> = [
		{
			label: 'Open',
			type: TASK_FILTER_CONSTANTS.OPEN,
		},
		{
			label: 'Due today',
			type: TASK_FILTER_CONSTANTS.DUE_TODAY,
		},
		{
			label: 'Due this week',
			type: TASK_FILTER_CONSTANTS.DUE_THIS_WEEK,
		},
		{
			label: 'Overdue',
			type: TASK_FILTER_CONSTANTS.OVERDUE,
		},
		{
			label: 'Done',
			type: TASK_FILTER_CONSTANTS.DONE,
		},
	];
	statusFilterModel: any = this.statusFilter[0].type;
	taskStatusIdsRequest: any = null;
	isRequestInProgress: boolean = false;
	isAdminOrOwner: boolean = false;
	totalTasks = 0;
	userId: number = null;

	constructor(
		private cargochief: Cargochief,
		public utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		public taskService: TaskService,
		private usersService: UsersService
	) {
		this.userId = this.cargochief.USER_ID;
	}

	ngOnInit() {
		this.isAdminOrOwner = this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner();
		if (!this.utilService.isNullOrUndefined(this.projectId)) {
			this.getTasks();
		}
	}

	ngOnChanges() {
		if (
			!this.utilService.isNullOrUndefined(this.carrier) &&
			!this.utilService.isNullOrUndefined(this.carrier.id)
		) {
			this.getTasks();
		}
	}

	getTasks() {
		this.isLoading = true;
		this.taskStatusIdsRequest = GET_TASK_FILTER(this.statusFilterModel, this.cargochief.ACCOUNT_ID);
		const url = this.urlService.getTasksForAccount(this.page, this.size);
		let payload = Object.assign({}, this.taskStatusIdsRequest);
		if (!this.utilService.isNullOrUndefined(this.projectId)) {
			payload['projectIds'] = [this.projectId];
		}
		if (
			!this.utilService.isNullOrUndefined(this.carrier) &&
			!this.utilService.isNullOrUndefined(this.carrier['id'])
		) {
			payload['carrierIds'] = [this.carrier['id']];
		}
		if (!this.isAdminOrOwner) {
			payload.assigneeId = this.cargochief.USER_ID;
		}
		this.restService.POST(url, payload).subscribe(
			(response) => {
				this.tasksList = TASK_TABLE(response['data']);
				this.hasNext = response.data.nextPage;
				this.isLoading = false;
				if (
					this.isAdminOrOwner &&
					!this.utilService.isNullOrUndefined(this.tasksList) &&
					this.tasksList.length > 0
				) {
					this.getUsersList();
				}
				this.emitLatestTaskCount(response.totalElements > 0 ? response.totalElements : 0);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to get tasks list for the project.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.isLoading = false;
			}
		);
	}

	public trackItem(index: number, task: TaskTable) {
		return `${task.id}`;
	}

	isListEmpty(): boolean {
		return !this.isLoading && (!this.tasksList || this.tasksList.length == 0);
	}

	onScroll() {
		if (this.hasNext) {
			this.page += 1;
			this.getTasks();
		}
	}

	cardClicked(task: TaskTable, event: MouseEvent) {
		document.getSelection().removeAllRanges();
		if (!event.shiftKey) {
			if (this.selectedTasksIds.indexOf(task.id) > -1) {
				this.selectedTasksIds.length = this.selectedTasks.length = 0;
			} else {
				this.selectedTasksIds.length = this.selectedTasks.length = 0;
				this.selectedTasksIds.push(task.id);
				this.selectedTasks = this.selectedTasks.concat([task]);
			}
		} else {
			if (this.selectedTasksIds.indexOf(task.id) > -1) {
				const matchedIndex = this.selectedTasksIds.indexOf(task.id);
				this.selectedTasksIds.splice(matchedIndex, 1);
				this.selectedTasks.splice(matchedIndex, 1);
				this.selectedTasks = [].concat(this.selectedTasks);
			} else {
				this.selectedTasksIds.push(task.id);
				this.selectedTasks = this.selectedTasks.concat([task]);
			}
		}
	}

	statusFilterChanged() {
		this.page = 0;
		this.tasksList.length = this.selectedTasksIds.length = this.selectedTasks.length = 0;
		this.taskStatusIdsRequest = GET_TASK_FILTER(this.statusFilterModel, this.cargochief.ACCOUNT_ID);
		this.getTasks();
	}

	deleteTasks() {
		const deletableTasks = this.taskService.filterDeletableTasks(this.selectedTasks, this.userId);
		if (deletableTasks.length == 0) {
			const warningMessage = this.taskService.getDeleteWarningMessage(
				this.selectedTasks.length,
				this.selectedTasks.length
			);
			this.utilService.showSnackbar(warningMessage, SNACKBAR.WARNING, SNACKBAR.ACTION);
			return;
		}
		const url = this.urlService.deleteTasks();
		this.isRequestInProgress = true;
		const payload = {
			taskIDs: deletableTasks.map((task) => task.id),
		};
		this.restService.DELETE(url, payload).subscribe(
			(response) => {
				const deletedTasks: Array<TaskDTO> = response.data;
				if (!this.utilService.isNullOrUndefined(deletedTasks) && deletedTasks.length > 0) {
					this.tasksList = this.tasksList.filter((task) => {
						return (
							deletedTasks.filter((delTask) => {
								return delTask.id == task.id;
							}).length == 0
						);
					});
					this.utilService.showSnackbar(
						`${deletedTasks.length} task(s) deleted successfully.`,
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
					if (deletableTasks.length != this.selectedTasks.length) {
						const warningMessage = this.taskService.getDeleteWarningMessage(
							this.selectedTasks.length - deletableTasks.length,
							this.selectedTasks.length
						);
						setTimeout(() => {
							this.utilService.showSnackbar(warningMessage, SNACKBAR.WARNING, SNACKBAR.ACTION);
						}, 1500);
					}
				} else {
					this.utilService.showSnackbar(
						'Unable to delete task now. Please try again.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
				this.emitLatestTaskCount(this.totalTasks - response.data.length);
				this.isRequestInProgress = false;
				this.removeDeletedTasksFromSelectedTasks(deletableTasks);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to delete task now. Please try again.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.isRequestInProgress = false;
			}
		);
	}

	taskAdded(task: any) {
		let addedTask: TaskTable = TASK_TABLE([task])[0];
		addedTask.assigneeName = task.assigneeName;
		this.tasksList = [addedTask].concat(this.tasksList);
		this.emitLatestTaskCount(this.totalTasks + 1);
	}

	getUsersList() {
		let userIds = this.tasksList.map(({ assigneeId }) => assigneeId);
		userIds = [...new Set(userIds)];
		if (userIds.length > 0) {
			const users = this.usersService.getUsersById(userIds);
			if (IS_NOT_NULL_OR_UNDEFINED(users) && users.length > 0) {
				this.tasksList.map((task: TaskTable) => {
					const user: any = users.filter((user) => {
						return user.id == task.assigneeId;
					});
					if (!this.utilService.isNullOrUndefined(user)) {
						task.assigneeName = `${IS_NOT_EMPTY(user[0].firstName) ? user[0].firstName : ''} ${
							IS_NOT_EMPTY(user[0].lastName) ? user[0].lastName : ''
						}`;
					} else {
						task.assigneeName = '';
					}
				});
			}
		}
	}

	updateTasks(payload: TaskUpdateRequest) {
		this.isRequestInProgress = true;
		let request = Object.assign({}, payload);
		delete request['assigneeName'];
		const url = this.urlService.bulkUpdateTasks();
		this.restService.PUT(url, request).subscribe(
			(response: C4ListResponse<TaskDTO>) => {
				const updatedTasks = TASK_TABLE(response['data']);
				const doneStatus = this.getDoneStatusString();
				let updatedTasksList = this.tasksList
					.map((task) => {
						let filteredTask = updatedTasks.find((updatedTask) => task.id == updatedTask.id);
						if (filteredTask) {
							filteredTask.assigneeName = !this.utilService.isNullOrUndefined(payload.assigneeId)
								? payload['assigneeName']
								: task.assigneeName;
							return filteredTask;
						}
						return task;
					})
					.filter((task) => {
						if (this.statusFilterModel.toLowerCase() != TASK_FILTER_CONSTANTS.DONE.toLowerCase()) {
							return (
								task.status !== doneStatus &&
								task.status != TASK_STATUS_UUID[TASK_STATUS_UUID_MAP.CANCELLED]
							);
						} else if (
							this.statusFilterModel.toLowerCase() == TASK_FILTER_CONSTANTS.DONE.toLowerCase()
						) {
							return task.status === doneStatus;
						}
					});
				if (
					!this.utilService.isNullOrUndefined(payload.projectId) &&
					!this.utilService.isNullOrUndefined(this.projectDTO) &&
					payload.projectId != this.projectDTO.id
				) {
					updatedTasksList = updatedTasksList.filter((task) => {
						return task.projectId != payload.projectId;
					});
					this.emitLatestTaskCount(this.totalTasks - 1);
				}
				if (
					!this.utilService.isNullOrUndefined(payload.carrierId) &&
					!this.utilService.isNullOrUndefined(this.carrier) &&
					!this.utilService.isNullOrUndefined(this.carrier.id) &&
					payload.carrierId != this.carrier.id
				) {
					updatedTasksList = updatedTasksList.filter((task) => {
						return task.carrierId != payload.carrierId;
					});
					this.emitLatestTaskCount(this.totalTasks - 1);
				}
				this.emitLatestTaskCount(
					this.totalTasks - (this.tasksList.length - updatedTasksList.length)
				);
				this.tasksList = [...updatedTasksList];
				this.utilService.showSnackbar(
					`${this.selectedTasksIds.length} tasks updated successfully.`,
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				this.selectedTasksIds = [];
				this.isRequestInProgress = false;
			},
			(error) => {
				this.utilService.showSnackbar('Unable to update tasks.', SNACKBAR.ERROR, SNACKBAR.ACTION);
				this.isRequestInProgress = false;
			}
		);
	}

	getDoneStatusString(): string {
		return TASK_STATUS_UUID[TASK_STATUS_UUID_MAP.DONE];
	}

	emitLatestTaskCount(currentVal: any) {
		this.totalTasks = currentVal;
		this.totalNumberOfTasks.emit(this.totalTasks > 0 ? this.totalTasks : '');
	}

	removeDeletedTasksFromSelectedTasks(deletedTasks: Array<TaskTable>) {
		this.selectedTasks = this.selectedTasks.filter((task) => {
			return deletedTasks.filter((deletedTask) => deletedTask.id == task.id).length == 0;
		});
	}
}
